import { useEffect, useState } from 'react'
import { Link, Outlet, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Empty, Flex, Spin, Typography } from 'antd'
import { ApiError } from '@/api/client'
import { loadAppIdentity } from '@/api/apps'
import { useAppStore, useUserStore } from '@/store'
import { confirmDisplayName } from '@/lib/appDisplayName'
import { isMockSpaceId } from '@/lib/currentOrg'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useLoadMySpaces } from '@/components/SpaceSwitcher'
import { useRuntimeStore } from '@/store/useRuntimeStore'
import AppDetailSider from './AppDetailSider'

const HEADER_HEIGHT = 56

function AppDetailLayout() {
  const { t } = useTranslation()
  const { app_id } = useParams()
  const app = useAppStore((s) => (app_id ? s.getApp(app_id) : undefined))
  const upsertApp = useAppStore((s) => s.upsertApp)
  const [booting, setBooting] = useState(() => Boolean(app_id && !app))
  const [bootError, setBootError] = useState<string | null>(null)
  const isClient = useRuntimeStore((s) => s.isClient)
  useLoadMySpaces()
  const spacesLoad = useUserStore((s) => s.spacesLoad)
  const spaceId = useUserStore((s) => s.space_id)
  const spaceReady =
    spacesLoad === 'ready' && Boolean(spaceId) && !isMockSpaceId(spaceId)

  useEffect(() => {
    if (spacesLoad !== 'error') return
    setBooting(false)
    setBootError(t('workbench.loadFailed'))
  }, [spacesLoad, t])

  useEffect(() => {
    if (!app_id || !spaceReady) return
    let cancelled = false
    const needBlock = !useAppStore.getState().getApp(app_id)
    if (needBlock) setBooting(true)
    setBootError(null)

    // 名称用缓存先画；身份接口只校正应用字段。版本列表由侧栏拉取。
    void loadAppIdentity(app_id)
      .then((identity) => {
        if (cancelled) return
        upsertApp(identity)
        confirmDisplayName(identity.app_id || identity.id, identity.name)
        setBootError(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // 已有缓存时静默失败，避免工作台点进来后被错误态盖掉。
        if (useAppStore.getState().getApp(app_id)) return
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err)
        setBootError(msg || t('workbench.loadFailed'))
      })
      .finally(() => {
        if (cancelled) return
        setBooting(false)
      })

    return () => {
      cancelled = true
    }
    // upsertApp / t 不稳定，勿放入依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app_id, spaceReady])

  const mainContent = (() => {
    if (app) return <Outlet context={{ app }} />
    if (booting) {
      return (
        <Flex align="center" justify="center" style={{ flex: 1, margin: 'auto' }}>
          <Spin tip={t('common.loading')} />
        </Flex>
      )
    }
    return (
      <Empty
        style={{ margin: 'auto' }}
        description={
          <span>
            {bootError ?? t('common.empty')}{' '}
            <Link to="/apps">{t('nav.workbench')}</Link>
          </span>
        }
      />
    )
  })()

  return (
    <div
      className={isClient ? 'devplat-layout--client' : 'devplat-layout--browser'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      {/* 对齐 org-console：PC 内嵌不渲顶栏，避免与宿主双顶栏 */}
      {!isClient && (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            height: HEADER_HEIGHT,
            flexShrink: 0,
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            zIndex: 20,
          }}
        >
          <Link to="/apps">
            <Flex align="center" gap={8}>
              <img src="/favicon.svg" alt="" className="h-7 w-7" />
              <Typography.Text strong>{t('common.appName')}</Typography.Text>
            </Flex>
          </Link>
          <div className="hdr-actions">
            <LanguageSwitcher />
          </div>
        </header>
      )}

      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {app && <AppDetailSider app={app} />}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'auto',
            padding: 0,
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {mainContent}
        </main>
      </div>
    </div>
  )
}

export default AppDetailLayout
